import classNames from 'classnames';
import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceCarouselWithImageAndInfo,
} from 'prismicio-types';
import { FC } from 'react';
import { ImageField } from '@prismicio/client';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';

import 'swiper/css/effect-fade';

import { PrismicRichText } from '@prismicio/react';
import { asText } from '@prismicio/helpers';
import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import {
  HeadingAsParagraph,
  htmlSerializerWithProps,
  iwtsPrimaryFields,
  toCtaButtonPropsFromItem,
  withIwtsFitContent,
} from '@/utils';
import { CtaButton, PrismicImageWithAlt, Slider } from '@/components';
import { resolveImageRadiusToken } from '../../../theme/tokens';

import {
  getIwtsContentAlignStyle,
  getIwtsIconTextOrderStyle,
  getIwtsStandaloneCtaClassName,
} from '../ImageWithTextSideBySide.components';
import sharedStyle from '../components.module.scss';
import style from './carouselWithImageAndInfo.module.scss';

export const CarouselWithImageAndInfoVariation: FC<{
  slice: ImageWithTextSideBySideSliceCarouselWithImageAndInfo &
    ImageWithTextSideBySideSlice;
}> = ({ slice }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);

  const {
    content_background_color,
    content_horizontal_align,
    content_vertical_align,
    content_full_width,
    description_color,
    heading_color,
    icon_as_heading_max_width,
    layout_direction,
    title_color,
    autoplay_image_carousel,
    section_background_color,
  } = slice.primary;
  const imageCornerPreset =
    'image_corner_preset' in slice.primary
      ? slice.primary.image_corner_preset
      : 'brand';

  const layoutDirection = layout_direction ?? 'left';
  const alignText = content_horizontal_align ?? 'center';
  const hasContentBackground = !!content_background_color;

  const renderImage = (image?: ImageField) => {
    return (
      <figure
        className={classNames(
          style.image,
          resolveImageRadiusToken(imageCornerPreset as string),
          {
            [style.image__default]: !image?.url,
          },
        )}
      >
        <PrismicImageWithAlt
          field={image}
          height={image?.dimensions?.height}
          showPlaceholderIfEmpty
          style={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
          width={image?.dimensions?.width}
        />
      </figure>
    );
  };

  if (!filteredItems?.length) {
    return null;
  }

  return (
    <Slider
      autoplay={
        autoplay_image_carousel
          ? {
              delay: 5000,
              disableOnInteraction: false,
              stopOnLastSlide: false,
            }
          : false
      }
      className={style.swiper_container}
      effect="fade"
      fadeEffect={{ crossFade: true }}
      loop={filteredItems.length > 1}
      modules={[Autoplay, EffectFade, Navigation, Pagination]}
      pagination={{
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
            index + 1
          }"></span>`,
      }}
      showSliderButtons={true}
      showSliderHeader={false}
      showSlidesOutsideContainer={true}
      slidesPerView={1}
      spaceBetween={0}
    >
      {filteredItems?.map((item, index) => {
        const cta = withIwtsFitContent(
          toCtaButtonPropsFromItem(item, iwtsPrimaryFields, 'primary'),
          item,
          slice.variation,
        );

        return (
          <div
            className={classNames(style.carousel_swiper_container, {
              [style.carousel_swiper_container__left]:
                layoutDirection === 'left',
              [style.carousel_swiper_container__right]:
                layoutDirection === 'right',
            })}
            key={index}
          >
            <div
              className={classNames(
                style.image_carousel_swiper,
                {
                  [style.image_carousel_swiper__full_width_left]:
                    !!content_full_width && layoutDirection === 'left',
                  [style.image_carousel_swiper__full_width_right]:
                    !!content_full_width && layoutDirection === 'right',
                },
                resolveImageRadiusToken(imageCornerPreset as string),
              )}
              key={index}
              style={{ background: section_background_color ?? undefined }}
            >
              {renderImage(item?.image)}
            </div>
            <div
              className={classNames(style.body, {
                [style.body__left]: layoutDirection === 'left',
                [style.body__right]: layoutDirection === 'right',
                [style.body__full_left]:
                  content_full_width && layoutDirection === 'left',
                [style.body__full_right]:
                  content_full_width && layoutDirection === 'right',
              })}
            >
              <div
                className={classNames(
                  style.content,
                  {
                    [style.content_with_background]: hasContentBackground,
                  },
                  hasContentBackground &&
                    resolveImageRadiusToken(imageCornerPreset as string),
                )}
                style={getIwtsContentAlignStyle({
                  background:
                    content_background_color ||
                    section_background_color ||
                    undefined,
                  contentHorizontalAlign: content_horizontal_align,
                  contentVerticalAlign: content_vertical_align,
                  layout: 'fullHeight',
                })}
              >
                {(item?.icon_as_heading?.url || asText(item?.heading)) && (
                  <div
                    className={style.body__heading_container}
                    style={getIwtsIconTextOrderStyle(alignText)}
                  >
                    {item?.icon_as_heading?.url && (
                      <figure
                        className={style.body__heading_icon}
                        style={{ maxWidth: icon_as_heading_max_width ?? '' }}
                      >
                        <PrismicImageWithAlt
                          defaultAlt="icon"
                          field={item?.icon_as_heading}
                          height={item?.icon_as_heading?.dimensions?.height}
                          style={{
                            objectFit: 'contain',
                            width: '100%',
                            height: 'auto',
                          }}
                          width={item?.icon_as_heading?.dimensions?.width}
                        />
                      </figure>
                    )}
                    <HeadingAsParagraph
                      className={style.body__text_heading}
                      color={heading_color as string}
                      field={item?.heading}
                    />
                  </div>
                )}
                {item?.title && (
                  <div
                    className={style.body__title_container}
                    style={getIwtsIconTextOrderStyle(alignText)}
                  >
                    <PrismicRichText
                      components={htmlSerializerWithProps(
                        {
                          color: title_color,
                        },
                        style.body__title,
                      )}
                      field={item?.title}
                    />
                  </div>
                )}
                {item?.description && (
                  <div className={sharedStyle.body__description_text}>
                    <PrismicRichText
                      components={htmlSerializerWithProps({
                        color: description_color,
                      })}
                      field={item?.description}
                    />
                  </div>
                )}
                {cta && (
                  <CtaButton
                    {...cta}
                    className={getIwtsStandaloneCtaClassName({
                      isLink: cta.variant === 'link',
                      fitContent: Boolean(cta.fitContent),
                    })}
                    fitContent={Boolean(cta.fitContent)}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </Slider>
  );
};
